import { BaseViewStore } from 'core/stores';
import { FulfillmentOrderStore } from 'modules/order/stores';
import { MarkAsShippedForm } from 'modules/order/forms';
import { observable, action, makeObservable } from 'mobx';
import { ProductInventoryStore } from 'modules/product-inventory/stores';
import { getDistinctProductIds, translate } from 'core/utils';
import { CarrierServiceStore } from 'modules/carrier/stores';
import { sourceOfChange } from 'common/constants';
import _ from 'lodash';

class MarkAsShippedViewStore extends BaseViewStore {
	@observable disableSaveBtn = true;
	@observable selectedWarehouse = null;
	@observable warehouseOptions = [];
	@observable selectedCarrierService = null;
	@observable carrierServiceOptions = [];
	@observable isInternalShipment = true;
	@observable pageWarehouse = 1;
	@observable tempCarrierServiceOptions = [];
	@observable pageCarrierService = 1;
	@observable isCarrierServiceLoading = false;
	@observable searchPhrase = null;
	@observable isWarehouseDropdownLoading = false;
	selectedItem = null;
	parentView = null;
	debouncedWarehouseSearch = null;
	debouncedCarrierServiceSearch = null;
	notProcessableItems = [];

	form = new MarkAsShippedForm({
		onSuccess: async () => {
			await this.markAsShipped();
		},
	});

	constructor(rootStore, { parentViewStore, selectedItemId }) {
		super(rootStore);
		makeObservable(this);
		this.parentView = parentViewStore;
		this.fulfillmentOrderStore = new FulfillmentOrderStore(rootStore);
		this.productInventoryStore = new ProductInventoryStore(rootStore);
		this.carrierServiceStore = new CarrierServiceStore(rootStore);
		this.debouncedWarehouseSearch = _.debounce((searchPhrase, { action }) => {
			this.searchWarehouseAsync(searchPhrase, { action });
		}, 500);
		this.debouncedCarrierServiceSearch = _.debounce((searchPhrase, { action }) => {
			this.searchCarrierServiceAsync(searchPhrase, { action });
		}, 500);
		this.initialize(selectedItemId);
	}

	@action.bound
	initialize = async selectedItemId => {
		this.suspend();
		this.form.state.options.set({
			validateOnBlur: false,
			showErrorsOnReset: false,
			validateOnChange: true,
		});
		await this.getFulfillmentOrder(selectedItemId);
		this.updateForm();
		await this.findWarehouse();
		await this.getCarrierServices();
		this.resume();
	};

	@action.bound
	getFulfillmentOrder = async selectedItemId => {
		this.selectedItem = await this.fulfillmentOrderStore.getFulfillmentOrder(selectedItemId, {
			embed: [
				'shipmentInfos',
				'shipmentInfos.shipmentInfoStatus',
				'warehouseOrganization',
				'carrierService',
				'fulfillmentOrderItems',
			],
		});
	};

	@action.bound
	updateForm = () => {
		if (this.selectedItem.shipmentInfos.length === 0) {
			this.form.$('cost').value = '';
			this.form.$('shippingCostSurcharge').value = '';
			this.form.$('trackingNumber').value = '';
		} else {
			const shipmentInfo = _.find(this.selectedItem.shipmentInfos, shipmentInfo => {
				return shipmentInfo.shipmentInfoStatus.abrv == 'active';
			});
			this.form.$('cost').value = shipmentInfo ? shipmentInfo.cost : '';
			this.form.$('shippingCostSurcharge').value = shipmentInfo ? shipmentInfo.shippingCostSurcharge : '';
			this.form.$('trackingNumber').value = shipmentInfo ? shipmentInfo.trackingNumber : '';
		}
		if (this.selectedItem.carrierService) {
			this.form.$('carrierService').value = {
				value: this.selectedItem.carrierService.id,
				label: this.selectedItem.carrierService.name,
				extra: this.selectedItem.carrierService,
			};
			this.selectedCarrierService = this.form.$('carrierService').value;
		}
		if (this.selectedItem.warehouseOrganization) {
			this.form.$('warehouse').value = {
				value: this.selectedItem.warehouseOrganization.id,
				label: this.selectedItem.warehouseOrganization.applicationTitle,
			};
			this.selectedWarehouse = this.form.$('warehouse').value;
		}
	};

	@action.bound
	async handleOnWarehouseMenuScroll() {
		if (this.warehouseOptions.length < this.pageWarehouse * 15) return;
		this.isWarehouseDropdownLoading = true;
		this.pageWarehouse++;
		const productIds = getDistinctProductIds(this.selectedItem.fulfillmentOrderItems);
		await this.productInventoryStore.findProductInventory({
			page: this.pageWarehouse,
			rpp: 15,
			search: this.searchPhrase,
			searchFields: 'assignedWarehouse.applicationTitle',
			fulfillmentOrderId: this.selectedItem.id,
			productIds: productIds,
			calculateInventory: true,
			isOrganizationActive: true,
			filterInapplicableWarehouses: true,
			includeOnlySellerWarehouses: true,
			sellerWarehousesOnlyOrganizationId: this.selectedItem.sellerId,
			embed: [
				'assignedWarehouse',
				'assignedWarehouse.warehouseOrganizationInfo',
				'assignedWarehouse.warehouseOrganizationInfo.address',
				'assignedWarehouse.warehouseOrganizationInfo.address.coreCountry',
				'assignedWarehouse.warehouseOrganizationInfo.address.coreCountryState',
			],
		});
		if (this.productInventoryStore.response.item.length > 0) {
			const newOptions = _.map(this.productInventoryStore.data, item => {
				return { value: item.assignedWarehouse.id, label: item.assignedWarehouse.applicationTitle };
			});
			this.warehouseOptions = this.warehouseOptions.concat(newOptions);
		}
		this.isWarehouseDropdownLoading = false;
	}

	@action.bound
	async handleOnCarrierServiceMenuScroll() {
		if (this.carrierServiceOptions.length < this.pageCarrierService * 15) return;
		this.isCarrierServiceLoading = true;
		this.pageCarrierService++;
		await this.carrierServiceStore.findCarrierService({
			page: this.pageCarrierService,
			rpp: 30,
		});
		this.tempCarrierServiceOptions = _.map(this.carrierServiceStore.data, item => {
			return { label: item.name, value: item.id };
		});
		if (this.tempCarrierServiceOptions.length > 0) {
			this.carrierServiceOptions = this.carrierServiceOptions.concat(this.tempCarrierServiceOptions);
			this.scrollLoadCarrierService = false;
		}
		this.isCarrierServiceLoading = false;
	}

	@action.bound
	async searchWarehouseAsync(searchPhrase, { action }) {
		if (searchPhrase === '') {
			this.pageWarehouse = 1;
		}
		if (action === 'input-change') {
			this.isWarehouseDropdownLoading = true;
			this.searchPhrase = searchPhrase;
			this.pageWarehouse = 1;
			this.warehouseOptions = [];
			const productIds = getDistinctProductIds(this.selectedItem.fulfillmentOrderItems);
			let filter = {
				rpp: 15,
				page: 1,
				productIds: productIds,
				searchFields: 'assignedWarehouse.applicationTitle',
				fulfillmentOrderId: this.selectedItem.id,
				calculateInventory: true,
				filterInapplicableWarehouses: true,
				isOrganizationActive: true,
				includeOnlySellerWarehouses: true,
				sellerWarehousesOnlyOrganizationId: this.selectedItem.sellerId,
				embed: [
					'assignedWarehouse',
					'assignedWarehouse.warehouseOrganizationInfo',
					'assignedWarehouse.warehouseOrganizationInfo.address',
					'assignedWarehouse.warehouseOrganizationInfo.address.coreCountry',
					'assignedWarehouse.warehouseOrganizationInfo.address.coreCountryState',
				],
			};
			await this.productInventoryStore.findProductInventory(filter);
			const newOptions = [];
			_.forEach(this.productInventoryStore.data, item => {
				newOptions.push({ value: item.assignedWarehouse.id, label: item.assignedWarehouse.applicationTitle });
			});
			this.warehouseOptions = newOptions;
			this.isWarehouseDropdownLoading = false;
		}
	}

	@action.bound
	async onMenuCloseWarehouse(item) {
		if (item) {
			this.selectedWarehouse = item;
		}
		this.pageWarehouse = 1;
		this.searchPhrase = null;
		await this.findWarehouse();
	}

	@action.bound
	async onMenuCloseCarrier(item) {
		if (item) {
			this.selectedCarrierService = item;
		}
		this.pageCarrierService = 1;
		this.searchPhrase = null;
		await this.getCarrierServices();
	}

	@action.bound
	async searchCarrierServiceAsync(searchPhrase, { action }) {
		if (searchPhrase === '') {
			this.pageCarrierService = 1;
		}
		if (action === 'input-change') {
			this.isCarrierServiceLoading = true;
			this.searchPhrase = searchPhrase;
			this.pageCarrierService = 1;
			this.carrierServiceOptions = [];
			await this.carrierServiceStore.findCarrierService({
				search: searchPhrase || null,
				rpp: 30,
				page: 1,
			});
			const newOptions = [];
			_.forEach(this.carrierServiceStore.data, item => {
				newOptions.push({ label: item.name, value: item.id });
			});
			this.carrierServiceOptions = newOptions;
			this.isCarrierServiceLoading = false;
		}
	}

	@action.bound async findWarehouse() {
		const productIds = getDistinctProductIds(this.selectedItem.fulfillmentOrderItems);
		await this.productInventoryStore.findProductInventory({
			page: 1,
			rpp: 15,
			fulfillmentOrderId: this.selectedItem.id,
			productIds: productIds,
			searchFields: 'assignedWarehouse.applicationTitle',
			calculateInventory: true,
			filterInapplicableWarehouses: true,
			isOrganizationActive: true,
			includeOnlySellerWarehouses: true,
			sellerWarehousesOnlyOrganizationId: this.selectedItem.sellerId,
			embed: [
				'assignedWarehouse',
				'assignedWarehouse.warehouseOrganizationInfo',
				'assignedWarehouse.warehouseOrganizationInfo.address',
				'assignedWarehouse.warehouseOrganizationInfo.address.coreCountry',
				'assignedWarehouse.warehouseOrganizationInfo.address.coreCountryState',
			],
		});
		this.warehouseOptions = _.map(this.productInventoryStore.data, item => {
			return { label: item.assignedWarehouse.applicationTitle, value: item.assignedWarehouse.id };
		});

		if (this.selectedWarehouse) {
			const matchingWarehouse = _.find(this.warehouseOptions, warehouse => {
				return warehouse.value === this.selectedWarehouse.value;
			});
			// If the currently assigned warehouse does not have available inventory
			if (!matchingWarehouse) {
				this.form.$('warehouse').value = {
					value: null,
					label: `${this.selectedWarehouse.label} ${translate(
						'FULFILLMENTORDER.MARKASSHIPPEDMODAL.NOAVAILABLEINVENTORY'
					)}`,
				};
			}
		}
	}

	@action.bound async getCarrierServices() {
		await this.carrierServiceStore.findCarrierService({
			page: 1,
			rpp: 30,
		});
		this.carrierServiceOptions = _.map(this.carrierServiceStore.data, item => {
			return { label: item.name, value: item.id };
		});
	}

	@action.bound
	markAsShipped = async () => {
		try {
			const { warehouse, trackingNumber, carrierService, cost, shippingCostSurcharge } = this.form.values();
			if (this.isInternalShipment) {
				this.selectedItem.warehouseId = warehouse.value;
				this.selectedItem.carrierServiceId = carrierService.value;
				this.trackingNumber = trackingNumber;
				this.cost = cost;
				this.shippingCostSurcharge = shippingCostSurcharge;
			} else {
				this.trackingNumber = '';
				this.cost = null;
				this.shippingCostSurcharge = 0;
				this.selectedItem.warehouseId = null;
				this.selectedItem.carrierServiceId = null;
				if (this.selectedItem.isChecked) {
					delete this.selectedItem.isChecked;
				}
			}
			await this.fulfillmentOrderStore.markAsShipped(
				{
					fulfillmentOrderId: this.selectedItem.id,
					trackingNumber: this.trackingNumber,
					cost: this.cost,
					shippingCostSurcharge: this.shippingCostSurcharge,
					assignedWarehouseId: this.selectedItem.warehouseId,
					assignedCarrierServiceId: this.selectedItem.carrierServiceId,
					isInternal: this.isInternalShipment,
				},
				sourceOfChange.markAsShipped
			);
			this.rootStore.notificationStore.success([
				'FULFILLMENTORDER.MARKASSHIPPEDMODAL.SUCCESS',
				{ id: this.selectedItem.fulfillmentOrderId },
			]);
			this.parentView.reload = true;
			this.parentView.markAsShippedModal.close();
		} catch (err) {
			if (err.statusCode == 400 && err.data == 'FULFILLMENTORDER.ALREADYPROCESSING') {
				this.rootStore.notificationStore.error([
					'FULFILLMENTORDER.ALREADYPROCESSING',
					{ id: this.selectedItem.fulfillmentOrderId },
				]);
			} else {
				this.rootStore.notificationStore.error([
					'FULFILLMENTORDER.MARKASSHIPPEDMODAL.FAIL',
					{ id: this.selectedItem.fulfillmentOrderId },
				]);
			}
		}
	};

	@action.bound setInputs(input, field) {
		const key = field.key + 'Input';
		this[key] = input;
	}

	@action.bound
	onWarehouseSelect = item => {
		if (item != null) {
			this.selectedWarehouse = item;
			this.form.$('warehouse').value = item;
		}
	};

	@action.bound
	onCarrierServiceSelect = item => {
		if (item != null) {
			this.selectedCarrierService = item;
			this.form.$('carrierService').value = item;
		}
	};

	@action.bound
	onSwitch = () => {
		this.isInternalShipment = !this.isInternalShipment;
		if (this.isInternalShipment) {
			this.form.$('trackingNumber').set('rules', 'required');
			this.form.$('cost').set('rules', 'required|numeric|decimalLength');
			this.form.$('shippingCostSurcharge').set('rules', 'numeric|decimalLength');
			this.form.$('warehouse').set('rules', 'required');
			this.form.$('carrierService').set('rules', 'required');
		} else {
			this.form.$('warehouse').set('rules', '');
			this.form.$('trackingNumber').set('rules', '');
			this.form.$('carrierService').set('rules', '');
			this.form.$('cost').set('rules', '');
			this.form.$('shippingCostSurcharge').set('rules', '');
		}
	};

	@action.bound
	onKeyUp = event => {
		if (event.target.name === 'trackingNumber') {
			const regex = /^[0-9\b]+$/;
			if (this.clearInputOnChange && !event.ctrlKey && regex.test(event.key)) {
				this.clearInputOnChange = false;
				this.form.$('trackingNumber').value = event.currentTarget.value.slice(-1);
			} else {
				const valueWithoutWhitespace = event.currentTarget.value.replace(/ /g, '');
				this.form.$('trackingNumber').value = valueWithoutWhitespace;
			}
		} else if (event.target.name === 'cost') {
			const regex = /^[0-9\b]+$/;
			if (this.clearInputOnChange && !event.ctrlKey && regex.test(event.key)) {
				this.clearInputOnChange = false;
				this.form.$('cost').value = event.currentTarget.value.slice(-1);
			} else {
				const valueWithoutWhitespace = event.currentTarget.value.replace(/ /g, '');
				this.form.$('cost').value = valueWithoutWhitespace;
			}
		} else if (event.target.name === 'shippingCostSurcharge') {
			const regex = /^[0-9\b]+$/;
			if (this.clearInputOnChange && !event.ctrlKey && regex.test(event.key)) {
				this.clearInputOnChange = false;
				this.form.$('shippingCostSurcharge').value = event.currentTarget.value.slice(-1);
			} else {
				const valueWithoutWhitespace = event.currentTarget.value.replace(/ /g, '');
				this.form.$('shippingCostSurcharge').value = valueWithoutWhitespace;
			}
		}
	};
}

export default MarkAsShippedViewStore;



// WEBPACK FOOTER //
// ./src/modules/order/stores/MarkAsShippedViewStore.js